import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card, PageHeader, Button, Modal, Input, Select, Spinner, SearchBar, EmptyState, StatusBadge } from "@/components/common";
import { Plus, Pencil, Trash2, IdCard } from "lucide-react";

const EMPTY = { person_id: "", numero_tessera: "", federazione: "", disciplina: "", categoria: "", stagione: "2025/2026", data_inizio: "", data_fine: "", assicurazione: "", note: "" };

export default function Tesseramenti() {
  const { can } = useAuth();
  const editable = can("tesseramenti", "edit");
  const [items, setItems] = useState(null);
  const [persone, setPersone] = useState([]);
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => {
    api.get("/tesseramenti").then((r) => setItems(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/persone").then((r) => setPersone(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (t) => { setForm({ ...EMPTY, ...t }); setEditId(t.id); setOpen(true); };
  const save = async () => {
    if (!form.person_id) { toast.error("Seleziona la persona"); return; }
    try {
      if (editId) await api.put(`/tesseramenti/${editId}`, form); else await api.post("/tesseramenti", form);
      toast.success("Salvato"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/tesseramenti/${id}`); load(); };

  if (!items) return <Spinner />;
  const filtered = items.filter((t) => `${t.person_name} ${t.numero_tessera} ${t.disciplina} ${t.federazione}`.toLowerCase().includes(q.toLowerCase()) && (!fStatus || t.status === fStatus));

  return (
    <div>
      <PageHeader title="Tesseramenti" subtitle="Identificazione via CF · durata max 12 mesi (RASD)"
        actions={editable && <Button onClick={openNew} data-testid="add-tess-btn"><Plus size={16} /> Nuovo tesseramento</Button>} />
      <div className="flex flex-wrap gap-3 mb-4">
        <SearchBar value={q} onChange={setQ} placeholder="Cerca tesserato..." />
        <Select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="w-auto"><option value="">Tutti gli stati</option><option value="green">Validi</option><option value="orange">In scadenza</option><option value="red">Scaduti</option></Select>
      </div>
      {filtered.length === 0 ? <Card className="p-6"><EmptyState icon={IdCard} title="Nessun tesseramento" /></Card> : (
        <Card className="overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200 bg-slate-50/60 text-left">
            {["Tesserato", "N° Tessera", "Federazione", "Disciplina", "Scadenza", "Stato", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold text-slate-500 uppercase text-xs tracking-wider">{h}</th>)}
          </tr></thead>
          <tbody>{filtered.map((t) => (
            <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`tess-row-${t.id}`}>
              <td className="px-4 py-3 font-semibold text-slate-800">{t.person_name}</td>
              <td className="px-4 py-3 font-mono text-xs text-slate-500">{t.numero_tessera || "—"}</td>
              <td className="px-4 py-3 text-slate-600">{t.federazione || "—"}</td>
              <td className="px-4 py-3 text-slate-600">{t.disciplina || "—"}</td>
              <td className="px-4 py-3 text-slate-600">{t.data_fine || "—"}</td>
              <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
              <td className="px-4 py-3 text-right">{editable && <div className="flex justify-end gap-1">
                <button onClick={() => openEdit(t)} data-testid={`edit-tess-${t.id}`} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil size={15} /></button>
                <button onClick={() => del(t.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={15} /></button></div>}</td>
            </tr>
          ))}</tbody>
        </table></div></Card>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifica tesseramento" : "Nuovo tesseramento"} wide>
        <div className="space-y-4">
          <Select label="Persona" data-testid="tess-person" value={form.person_id} onChange={(e) => upd("person_id", e.target.value)}>
            <option value="">Seleziona...</option>{persone.map((p) => <option key={p.id} value={p.id}>{p.cognome} {p.nome}</option>)}
          </Select>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Numero tessera" value={form.numero_tessera} onChange={(e) => upd("numero_tessera", e.target.value)} />
            <Input label="Federazione / EPS" value={form.federazione} onChange={(e) => upd("federazione", e.target.value)} placeholder="FIGC, FIPAV, CSI..." />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <Input label="Disciplina" value={form.disciplina} onChange={(e) => upd("disciplina", e.target.value)} />
            <Input label="Categoria" value={form.categoria} onChange={(e) => upd("categoria", e.target.value)} />
            <Input label="Stagione" value={form.stagione} onChange={(e) => upd("stagione", e.target.value)} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Data inizio" type="date" value={form.data_inizio} onChange={(e) => upd("data_inizio", e.target.value)} />
            <Input label="Data fine" type="date" value={form.data_fine} onChange={(e) => upd("data_fine", e.target.value)} />
          </div>
          <Input label="Assicurazione" value={form.assicurazione} onChange={(e) => upd("assicurazione", e.target.value)} />
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={save} data-testid="save-tess-btn">Salva</Button></div>
        </div>
      </Modal>
    </div>
  );
}
